import PropTypes from 'prop-types'
import { Box, Flex, IconButton, Image, Progress, Text, useDisclosure } from '@chakra-ui/react'
import { MdDeleteOutline, MdDownload, MdOutlineFilePresent, MdOutlinePictureAsPdf } from 'react-icons/md'
import { getAssetUrl } from 'lib'
import { ModalConfirmation } from 'components/common'
import { useState } from 'react'

export const FILE_ATTACHMENT_STATUS = {
  Uploading: 'uploading', // file is uploading
  Fail: 'fail', // file upload failed
  Success: 'success', // file upload success
  Original: 'original' // means that it's already saved on the server and db
}

export const FileAttachment = ({ canRemove, id, status, progress = 0, filename = '', extension = '', path = '', onRemove, canDownload = false }) => {
  const [isRemoving, setIsRemoving] = useState(false)
  const disclosureRemove = useDisclosure()

  const onOpenFile = () => {
    if (status === FILE_ATTACHMENT_STATUS.Original || status === FILE_ATTACHMENT_STATUS.Success) {
      window.open(getAssetUrl(path))
    }
  }

  const handleRemove = async () => {
    setIsRemoving(true)
    try {
      await onRemove(id, status, path)
    } catch (err) {
    }
    setIsRemoving(false)
  }

  const getFileIcon = (ext) => {
    if (extension === 'pdf') {
      return <MdOutlinePictureAsPdf fontSize="24px" />
    } else if (extension === 'png' || extension === 'jpg' || extension === 'jpeg') {
      return <Image src={getAssetUrl(path)} height="60px" width="60px" />
    } else {
      return <MdOutlineFilePresent fontSize="24px" />
    }
  }

  const handleDownload = async () => {
    const response = await fetch(getAssetUrl(path))
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Flex
        position="relative"
        alignItems="center"
        p={4}
        gap={2}
        borderWidth={1}
        borderStyle="solid"
        borderColor="gray.400"
        maxW="200px"
        minW="150px"
      >
        <Box flex={1}>
          {getFileIcon(extension)}
        </Box>
        <Box flex={4} overflow="hidden">
          {status === FILE_ATTACHMENT_STATUS.Uploading && (
            <Progress size='xs' value={progress} colorScheme="blue" />
          )}
          {status === FILE_ATTACHMENT_STATUS.Fail && (
            <Text color="red">Fail!</Text>
          )}
          {status === FILE_ATTACHMENT_STATUS.Success && (
            <Text color="green">Success!</Text>
          )}
          <Text fontSize="xs" cursor="pointer" onClick={onOpenFile}>{filename}.{extension}</Text>
        </Box>
        {canRemove && (
          <IconButton
            position="absolute"
            top={2}
            right={2}
            variant="ghost"
            size="xs"
            colorScheme="gray"
            icon={<MdDeleteOutline fontSize="18px" />}
            onClick={disclosureRemove.onOpen}
          />
        )}
        {canDownload && (
          <IconButton
            position="absolute"
            bottom={2}
            right={2}
            variant="ghost"
            size="xs"
            colorScheme="gray"
            icon={<MdDownload fontSize="18px" />}
            onClick={handleDownload}
          />
        )}
      </Flex>
      <ModalConfirmation
        heading="Remove Attachment"
        description="Are you sure to remove the file?"
        btnConfirmLabel="Yes"
        btnCancelLabel="No"
        onConfirm={handleRemove}
        isConfirming={isRemoving}
        {...disclosureRemove}
      />
    </>
  )
}

FileAttachment.propTypes = {
  canRemove: PropTypes.bool,
  id: PropTypes.number,
  status: PropTypes.string,
  progress: PropTypes.number,
  filename: PropTypes.string,
  extension: PropTypes.string,
  path: PropTypes.string,
  onRemove: PropTypes.func,
  canDownload: PropTypes.bool
}
